import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import {
  Drawer,
  Title,
  Text,
  Group,
  Badge,
  Stack,
  Divider,
  Timeline,
  Anchor,
  Box,
  Image,
  Button,
  Select,
  TextInput,
  Checkbox,
  Textarea,
  ActionIcon,
  Paper,
  Table,
  Alert,
  Switch,
  Modal,
} from '@mantine/core';
import {
  IconPackage,
  IconTruck,
  IconMapPin,
  IconUser,
  IconClock,
  IconShoppingCart,
  IconRefresh,
  IconHeadset,
  IconPlus,
  IconTrash,
  IconChevronUp,
  IconChevronDown,
  IconPaw,
  IconCheck,
  IconX,
  IconMail,
} from '@tabler/icons-react';
import type { Shipment, RiskLevel, CustomerTier, Market, CSStatus, CSNote, ExceptionSubtype, PetProfile, MealPlan } from '../types';

// Province/State abbreviations
const REGION_ABBREV: Record<string, string> = {
  'Ontario': 'ON',
  'Quebec': 'QC',
  'British Columbia': 'BC',
  'Alberta': 'AB',
  'Manitoba': 'MB',
  'Saskatchewan': 'SK',
  'Nova Scotia': 'NS',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Prince Edward Island': 'PE',
  'Northwest Territories': 'NT',
  'Yukon': 'YT',
  'Nunavut': 'NU',
  'Pennsylvania': 'PA',
  'New York': 'NY',
  'New Jersey': 'NJ',
  'Massachusetts': 'MA',
  'Connecticut': 'CT',
  'Maryland': 'MD',
  'Virginia': 'VA',
  'Ohio': 'OH',
  'Michigan': 'MI',
  'Florida': 'FL',
  'California': 'CA',
  'Texas': 'TX',
};

// DTC Warehouse coordinates
const DTC_COORDS: Record<Market, { lat: number; lon: number }> = {
  canada: { lat: 43.65, lon: -79.55 }, // Etobicoke
  usa: { lat: 40.15, lon: -74.92 },    // Pennsylvania
};

// City-level coordinates for common destinations
const CITY_COORDS: Record<string, { lat: number; lon: number }> = {
  // Ontario cities
  'toronto': { lat: 43.65, lon: -79.38 },
  'ottawa': { lat: 45.42, lon: -75.69 },
  'mississauga': { lat: 43.59, lon: -79.64 },
  'brampton': { lat: 43.73, lon: -79.76 },
  'hamilton': { lat: 43.26, lon: -79.87 },
  'london': { lat: 42.98, lon: -81.25 },
  'markham': { lat: 43.86, lon: -79.34 },
  'vaughan': { lat: 43.84, lon: -79.51 },
  'kitchener': { lat: 43.45, lon: -80.49 },
  'windsor': { lat: 42.31, lon: -83.04 },
  'richmond hill': { lat: 43.87, lon: -79.44 },
  'oakville': { lat: 43.45, lon: -79.68 },
  'burlington': { lat: 43.33, lon: -79.79 },
  'greater sudbury': { lat: 46.49, lon: -81.01 },
  'sudbury': { lat: 46.49, lon: -81.01 },
  'barrie': { lat: 44.39, lon: -79.69 },
  'oshawa': { lat: 43.90, lon: -78.86 },
  'st. catharines': { lat: 43.16, lon: -79.24 },
  'cambridge': { lat: 43.36, lon: -80.31 },
  'kingston': { lat: 44.23, lon: -76.48 },
  'guelph': { lat: 43.55, lon: -80.25 },
  'thunder bay': { lat: 48.38, lon: -89.25 },
  'waterloo': { lat: 43.46, lon: -80.52 },
  'brantford': { lat: 43.14, lon: -80.26 },
  'pickering': { lat: 43.84, lon: -79.09 },
  'niagara falls': { lat: 43.09, lon: -79.08 },
  'peterborough': { lat: 44.30, lon: -78.32 },
  'sault ste. marie': { lat: 46.52, lon: -84.35 },
  'newmarket': { lat: 44.06, lon: -79.46 },
  'ajax': { lat: 43.85, lon: -79.03 },
  'whitby': { lat: 43.90, lon: -78.94 },
  'milton': { lat: 43.52, lon: -79.88 },
  'etobicoke': { lat: 43.65, lon: -79.55 },
  'scarborough': { lat: 43.77, lon: -79.26 },
  'north york': { lat: 43.77, lon: -79.41 },
  'nepean': { lat: 45.35, lon: -75.73 },
  'concord': { lat: 43.81, lon: -79.49 },
  'thornhill': { lat: 43.81, lon: -79.42 },
  'aurora': { lat: 44.01, lon: -79.47 },
  'stouffville': { lat: 43.97, lon: -79.25 },
  'orangeville': { lat: 43.92, lon: -80.10 },
  'collingwood': { lat: 44.50, lon: -80.22 },
  'orillia': { lat: 44.61, lon: -79.42 },
  'belleville': { lat: 44.16, lon: -77.38 },
  'sarnia': { lat: 42.97, lon: -82.40 },
  'chatham': { lat: 42.40, lon: -82.18 },
  'stratford': { lat: 43.37, lon: -80.98 },
  'woodstock': { lat: 43.13, lon: -80.75 },
  'st. thomas': { lat: 42.77, lon: -81.18 },
  'leamington': { lat: 42.05, lon: -82.60 },
  'timmins': { lat: 48.48, lon: -81.33 },
  'north bay': { lat: 46.31, lon: -79.46 },
  'cornwall': { lat: 45.02, lon: -74.73 },
  'welland': { lat: 42.99, lon: -79.25 },
  'port colborne': { lat: 42.89, lon: -79.25 },
  'fort erie': { lat: 42.91, lon: -78.93 },
  'grimsby': { lat: 43.20, lon: -79.56 },
  'stoney creek': { lat: 43.22, lon: -79.77 },
  'ancaster': { lat: 43.22, lon: -79.99 },
  'dundas': { lat: 43.27, lon: -79.96 },
  'georgetown': { lat: 43.65, lon: -79.92 },
  'halton hills': { lat: 43.63, lon: -79.95 },
  'caledon': { lat: 43.87, lon: -79.87 },
  'bolton': { lat: 43.88, lon: -79.73 },
  'innisfil': { lat: 44.30, lon: -79.58 },
  'wasaga beach': { lat: 44.52, lon: -80.02 },
  'midland': { lat: 44.75, lon: -79.88 },
  'penetanguishene': { lat: 44.77, lon: -79.93 },
  'gravenhurst': { lat: 44.92, lon: -79.37 },
  'bracebridge': { lat: 45.03, lon: -79.31 },
  'huntsville': { lat: 45.33, lon: -79.22 },
  'parry sound': { lat: 45.34, lon: -80.03 },
  'cobourg': { lat: 43.96, lon: -78.17 },
  'port hope': { lat: 43.95, lon: -78.29 },
  'bowmanville': { lat: 43.91, lon: -78.69 },
  'clarington': { lat: 43.94, lon: -78.61 },
  'uxbridge': { lat: 44.11, lon: -79.12 },
  'lindsay': { lat: 44.35, lon: -78.74 },
  'kawartha lakes': { lat: 44.35, lon: -78.74 },
  'trenton on': { lat: 44.10, lon: -77.58 },
  'quinte west': { lat: 44.18, lon: -77.57 },
  'napanee': { lat: 44.25, lon: -76.95 },
  'brockville': { lat: 44.59, lon: -75.68 },
  'pembroke': { lat: 45.83, lon: -77.11 },
  'petawawa': { lat: 45.90, lon: -77.28 },
  'kanata': { lat: 45.30, lon: -75.90 },
  'orleans': { lat: 45.47, lon: -75.52 },
  'gloucester': { lat: 45.40, lon: -75.55 },

  // Quebec cities
  'montreal': { lat: 45.50, lon: -73.57 },
  'quebec city': { lat: 46.81, lon: -71.21 },
  'laval': { lat: 45.57, lon: -73.69 },
  'gatineau': { lat: 45.48, lon: -75.70 },
  'longueuil': { lat: 45.54, lon: -73.51 },
  'sherbrooke': { lat: 45.40, lon: -71.89 },
  'saguenay': { lat: 48.43, lon: -71.07 },
  'levis': { lat: 46.80, lon: -71.18 },
  'trois-rivieres': { lat: 46.34, lon: -72.54 },
  'terrebonne': { lat: 45.70, lon: -73.65 },
  'saint-jean-sur-richelieu': { lat: 45.31, lon: -73.27 },
  'brossard': { lat: 45.45, lon: -73.47 },
  'repentigny': { lat: 45.74, lon: -73.45 },
  'drummondville': { lat: 45.88, lon: -72.49 },
  'granby': { lat: 45.40, lon: -72.73 },
  'saint-hyacinthe': { lat: 45.63, lon: -72.96 },
  'mascouche': { lat: 45.75, lon: -73.60 },

  // British Columbia cities
  'vancouver': { lat: 49.28, lon: -123.12 },
  'surrey': { lat: 49.19, lon: -122.85 },
  'burnaby': { lat: 49.25, lon: -122.95 },
  'richmond': { lat: 49.17, lon: -123.14 },
  'victoria': { lat: 48.43, lon: -123.37 },
  'kelowna': { lat: 49.89, lon: -119.50 },
  'abbotsford': { lat: 49.05, lon: -122.33 },
  'coquitlam': { lat: 49.28, lon: -122.79 },
  'nanaimo': { lat: 49.17, lon: -123.94 },
  'kamloops': { lat: 50.67, lon: -120.33 },
  'chilliwack': { lat: 49.16, lon: -121.95 },

  // Alberta cities
  'calgary': { lat: 51.05, lon: -114.07 },
  'edmonton': { lat: 53.55, lon: -113.49 },
  'red deer': { lat: 52.27, lon: -113.81 },
  'lethbridge': { lat: 49.70, lon: -112.83 },
  'medicine hat': { lat: 50.04, lon: -110.68 },

  // Manitoba cities
  'winnipeg': { lat: 49.90, lon: -97.14 },
  'brandon': { lat: 49.85, lon: -99.95 },

  // Saskatchewan cities
  'saskatoon': { lat: 52.13, lon: -106.67 },
  'regina': { lat: 50.45, lon: -104.62 },

  // Nova Scotia cities
  'halifax': { lat: 44.65, lon: -63.58 },
  'dartmouth': { lat: 44.67, lon: -63.57 },

  // New Brunswick cities
  'saint john': { lat: 45.27, lon: -66.06 },
  'moncton': { lat: 46.09, lon: -64.78 },
  'fredericton': { lat: 45.96, lon: -66.64 },

  // Newfoundland cities
  "st. john's": { lat: 47.56, lon: -52.71 },
  'mount pearl': { lat: 47.52, lon: -52.80 },

  // PEI cities
  'charlottetown': { lat: 46.24, lon: -63.13 },

  // US cities (Pennsylvania and nearby)
  'philadelphia': { lat: 39.95, lon: -75.17 },
  'pittsburgh': { lat: 40.44, lon: -80.00 },
  'allentown': { lat: 40.60, lon: -75.49 },
  'reading': { lat: 40.34, lon: -75.93 },
  'erie': { lat: 42.13, lon: -80.09 },
  'harrisburg': { lat: 40.27, lon: -76.88 },
  'scranton': { lat: 41.41, lon: -75.66 },
  'bethlehem': { lat: 40.63, lon: -75.37 },
  'new york': { lat: 40.71, lon: -74.01 },
  'brooklyn': { lat: 40.65, lon: -73.95 },
  'queens': { lat: 40.73, lon: -73.87 },
  'manhattan': { lat: 40.78, lon: -73.97 },
  'bronx': { lat: 40.85, lon: -73.87 },
  'staten island': { lat: 40.58, lon: -74.15 },
  'buffalo': { lat: 42.89, lon: -78.88 },
  'rochester': { lat: 43.16, lon: -77.61 },
  'syracuse': { lat: 43.05, lon: -76.15 },
  'albany': { lat: 42.65, lon: -73.76 },
  'newark': { lat: 40.74, lon: -74.17 },
  'jersey city': { lat: 40.73, lon: -74.07 },
  'paterson': { lat: 40.92, lon: -74.17 },
  'trenton': { lat: 40.22, lon: -74.76 },
  'boston': { lat: 42.36, lon: -71.06 },
  'worcester': { lat: 42.26, lon: -71.80 },
  'springfield': { lat: 42.10, lon: -72.59 },
  'baltimore': { lat: 39.29, lon: -76.61 },
  'washington': { lat: 38.91, lon: -77.04 },
  'columbus': { lat: 39.96, lon: -83.00 },
  'cleveland': { lat: 41.50, lon: -81.69 },
  'cincinnati': { lat: 39.10, lon: -84.51 },
  'toledo': { lat: 41.65, lon: -83.54 },
  'akron': { lat: 41.08, lon: -81.52 },
  'detroit': { lat: 42.33, lon: -83.05 },
  'grand rapids': { lat: 42.96, lon: -85.66 },
  'ann arbor': { lat: 42.28, lon: -83.74 },
  'chicago': { lat: 41.88, lon: -87.63 },
};

// Fallback province/state centroids (used when city not found)
const REGION_COORDS: Record<string, { lat: number; lon: number }> = {
  'Ontario': { lat: 44.0, lon: -79.0 },
  'Quebec': { lat: 46.8, lon: -71.2 },
  'British Columbia': { lat: 49.3, lon: -123.1 },
  'Alberta': { lat: 51.0, lon: -114.1 },
  'Manitoba': { lat: 49.9, lon: -97.1 },
  'Saskatchewan': { lat: 52.1, lon: -106.7 },
  'Nova Scotia': { lat: 44.6, lon: -63.6 },
  'New Brunswick': { lat: 46.5, lon: -66.2 },
  'Newfoundland and Labrador': { lat: 47.6, lon: -52.7 },
  'Prince Edward Island': { lat: 46.2, lon: -63.1 },
  'Pennsylvania': { lat: 40.0, lon: -77.0 },
  'New York': { lat: 42.2, lon: -74.9 },
  'New Jersey': { lat: 40.1, lon: -74.7 },
  'Massachusetts': { lat: 42.4, lon: -71.4 },
  'Ohio': { lat: 40.4, lon: -82.9 },
  'Michigan': { lat: 44.3, lon: -85.6 },
  'Florida': { lat: 27.8, lon: -81.8 },
  'California': { lat: 36.8, lon: -119.4 },
  'Texas': { lat: 31.0, lon: -100.0 },
  'Maryland': { lat: 39.05, lon: -76.64 },
  'Virginia': { lat: 37.43, lon: -78.66 },
  'Connecticut': { lat: 41.60, lon: -72.70 },
  'Illinois': { lat: 40.63, lon: -89.40 },
};

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

function getDistanceFromDTC(city: string, province: string, market: Market): number | null {
  const dtcCoords = DTC_COORDS[market];

  // Try city-level coordinates first
  const normalizedCity = city.toLowerCase().trim();
  const cityCoords = CITY_COORDS[normalizedCity];
  if (cityCoords) {
    return calculateDistance(dtcCoords.lat, dtcCoords.lon, cityCoords.lat, cityCoords.lon);
  }

  // Fall back to province/state centroid
  const regionCoords = REGION_COORDS[province];
  if (regionCoords) {
    return calculateDistance(dtcCoords.lat, dtcCoords.lon, regionCoords.lat, regionCoords.lon);
  }

  return null;
}

function getCoordsForLocation(location: string): { lat: number; lon: number } | null {
  if (!location) return null;

  // Try to extract city from location string (e.g., "Mississauga, ON" or "Toronto")
  const normalizedLocation = location.toLowerCase().trim();

  // Try direct match first
  if (CITY_COORDS[normalizedLocation]) {
    return CITY_COORDS[normalizedLocation];
  }

  // Try first part before comma
  const parts = normalizedLocation.split(',');
  const cityPart = parts[0].trim();
  if (CITY_COORDS[cityPart]) {
    return CITY_COORDS[cityPart];
  }

  // Try matching any known city in the string
  for (const [cityName, coords] of Object.entries(CITY_COORDS)) {
    if (normalizedLocation.includes(cityName)) {
      return coords;
    }
  }

  return null;
}

function getDistanceToDestination(currentLocation: string, destCity: string): number | null {
  const currentCoords = getCoordsForLocation(currentLocation);
  const destCoords = getCoordsForLocation(destCity);

  if (!currentCoords || !destCoords) return null;

  return calculateDistance(currentCoords.lat, currentCoords.lon, destCoords.lat, destCoords.lon);
}

interface ShipmentDetailProps {
  shipment: Shipment | null;
  opened: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const RISK_COLORS: Record<RiskLevel, string> = {
  low: 'green',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
};

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  critical: 'WARNING',
};

const TIER_BADGES: Record<CustomerTier, { color: string; label: string }> = {
  vip: { color: 'grape', label: 'VIP' },
  high: { color: 'orange', label: 'HIGH' },
  medium: { color: 'blue', label: 'MED' },
  standard: { color: 'gray', label: '' },
};

const CARRIER_NAMES: Record<string, string> = {
  ecom: 'Ecom Logistics',
  loomis: 'Loomis Express',
  'gtagsm-loomis': 'GTA GSM (Loomis)',
  'gtagsm-e24': 'GTA GSM (E24)',
  gtagsm: 'GTA GSM',
  purolator: 'Purolator',
  ups: 'UPS',
};

const STATUS_LABELS: Record<string, string> = {
  awaiting_fulfillment: 'Awaiting Ship',
  pending: 'Pending',
  picked_up: 'Picked Up',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  exception: 'Exception',
};

const CS_STATUS_OPTIONS: { value: CSStatus; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'resolved', label: 'Resolved' },
];

const CS_STATUS_COLORS: Record<CSStatus, string> = {
  none: 'gray',
  monitoring: 'blue',
  contacted: 'yellow',
  escalated: 'orange',
  resolved: 'green',
};

const CS_STAFF_OPTIONS = [
  { value: '', label: '(Unassigned)' },
  { value: 'Derin', label: 'Derin' },
  { value: 'Ash', label: 'Ash' },
  { value: 'Celia Gatto', label: 'Celia' },
  { value: 'Kristin', label: 'Kristin' },
  { value: 'Peter Zakarow', label: 'Peter' },
];

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateOnly(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(amount);
}

function titleCase(str: string): string {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Format location preserving abbreviations (province codes, postal codes, DC)
function formatLocation(str: string): string {
  if (!str) return str;

  return str
    .split(/(\s+|,)/) // Split by spaces and commas, keeping delimiters
    .map(part => {
      // Keep delimiters as-is
      if (/^[\s,]+$/.test(part)) return part;

      // Preserve all-caps words 2-3 chars (province codes: ON, BC, PA, DC)
      if (part.length <= 3 && part === part.toUpperCase() && /^[A-Z]+$/.test(part)) {
        return part;
      }

      // Preserve Canadian postal codes (A1A format or 1A1 format)
      if (/^[A-Z]\d[A-Z]$/i.test(part) || /^\d[A-Z]\d$/i.test(part)) {
        return part.toUpperCase();
      }

      // Preserve US ZIP codes (numbers only)
      if (/^\d+$/.test(part)) {
        return part;
      }

      // Apply title case to regular words (city names)
      if (part.length > 0) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }

      return part;
    })
    .join('');
}

function calculateTimeInTransit(shipment: Shipment): string {
  // Need picked_up_at to calculate time in transit
  if (!shipment.picked_up_at) return '-';

  // End time: delivered_at if delivered, otherwise now
  const endTime = shipment.is_delivered && shipment.delivered_at
    ? shipment.delivered_at
    : new Date();

  const diffMs = endTime.getTime() - shipment.picked_up_at.getTime();
  if (diffMs < 0) return '-';

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Calculate actual deadline by shifting the estimated deadline
 * by the difference between actual pickup and expected ship date.
 * This preserves the backend's business-day logic.
 */
function getActualDeadline(shipment: Shipment): Date | null {
  if (!shipment.picked_up_at || !shipment.expected_ship_date || !shipment.deadline) return null;
  const expectedShip = shipment.expected_ship_date instanceof Date
    ? shipment.expected_ship_date
    : new Date(shipment.expected_ship_date);
  const pickedUp = shipment.picked_up_at instanceof Date
    ? shipment.picked_up_at
    : new Date(shipment.picked_up_at);
  const shiftMs = pickedUp.getTime() - expectedShip.getTime();
  return new Date(shipment.deadline.getTime() + shiftMs);
}

function buildMapUrl(address: {
  address1: string;
  address2: string | null;
  city: string;
  province: string;
  country: string;
  postal_code: string;
}): string {
  const parts = [address.address1];
  if (address.address2) parts.push(address.address2);
  parts.push(`${address.city}, ${address.province} ${address.postal_code}`);
  parts.push(address.country);
  const query = encodeURIComponent(parts.join(', '));
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

function buildStaticMapUrl(address: {
  address1: string;
  address2: string | null;
  city: string;
  province: string;
  country: string;
  postal_code: string;
}): string | null {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;

  const parts = [address.address1];
  if (address.address2) parts.push(address.address2);
  parts.push(`${address.city}, ${address.province} ${address.postal_code}`);
  parts.push(address.country);
  const query = encodeURIComponent(parts.join(', '));
  return `https://maps.googleapis.com/maps/api/staticmap?center=${query}&zoom=11&size=600x300&scale=2&maptype=roadmap&markers=color:red%7C${query}&key=${apiKey}`;
}

export function ShipmentDetail({
  shipment,
  opened,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}: ShipmentDetailProps) {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [savingCS, setSavingCS] = useState(false);
  const [saveCSResult, setSaveCSResult] = useState<{ success: boolean; message: string } | null>(null);

  // Pet profiles and meal plans state
  const [petProfiles, setPetProfiles] = useState<PetProfile[]>([]);
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [selectedPetId, setSelectedPetId] = useState<string | null>(null);
  const [loadingPets, setLoadingPets] = useState(false);

  // CS form state
  const [csStatus, setCsStatus] = useState<CSStatus>('none');
  const [csAssignedTo, setCsAssignedTo] = useState('');
  const [csContactedCustomer, setCsContactedCustomer] = useState(false);
  const [csContactedCarrier, setCsContactedCarrier] = useState(false);
  const [csResolution, setCsResolution] = useState('');
  const [csNotes, setCsNotes] = useState<CSNote[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [csIsException, setCsIsException] = useState(false);
  const [csExceptionSubtype, setCsExceptionSubtype] = useState<ExceptionSubtype | null>(null);
  const [csHasChanges, setCsHasChanges] = useState(false);

  // Delay notification state
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyReason, setNotifyReason] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState<{ success: boolean; message: string } | null>(null);

  // Sync CS form state with shipment data when shipment changes
  useEffect(() => {
    // Clear results when switching shipments
    setSyncResult(null);
    setSaveCSResult(null);

    // Always reset CS state when shipment changes - use defaults if no cs data
    setCsStatus(shipment?.cs?.status || 'none');
    setCsAssignedTo(shipment?.cs?.assigned_to || '');
    setCsContactedCustomer(shipment?.cs?.contacted_customer || false);
    setCsContactedCarrier(shipment?.cs?.contacted_carrier || false);
    setCsResolution(shipment?.cs?.resolution || '');
    setCsNotes(shipment?.cs?.notes || []);
    setCsIsException(shipment?.status === 'exception');
    setCsExceptionSubtype(shipment?.exception_subtype || null);
    setCsHasChanges(false);
    setNotifyModalOpen(false);
    setNotifyReason('');
    setNotifyResult(null);
  }, [shipment?.id, shipment?.cs]);

  // Fetch pet profiles and meal plans when shipment changes
  useEffect(() => {
    if (!shipment?.id || !shipment?.customer?.shopify_customer_id) {
      setPetProfiles([]);
      setMealPlans([]);
      setSelectedPetId(null);
      return;
    }

    const fetchPetData = async () => {
      setLoadingPets(true);
      try {
        const apiUrl = import.meta.env.VITE_API_URL ?? '';
        const response = await apiFetch(`${apiUrl}/api/shipments/${shipment.id}/pet-profiles`);
        if (response.ok) {
          const json = await response.json();
          const profiles = json.data || [];
          const plans = json.mealPlans || [];
          setPetProfiles(profiles);
          setMealPlans(plans);
          setSelectedPetId(profiles.length > 0 ? profiles[0].id : null);
        } else {
          setPetProfiles([]);
          setMealPlans([]);
          setSelectedPetId(null);
        }
      } catch (error) {
        console.error('Failed to fetch pet data:', error);
        setPetProfiles([]);
        setMealPlans([]);
        setSelectedPetId(null);
      } finally {
        setLoadingPets(false);
      }
    };

    fetchPetData();
  }, [shipment?.id, shipment?.customer?.shopify_customer_id]);

  if (!shipment) return null;

  const shopifyUrl = `https://admin.shopify.com/store/tom-sawyer/orders/${shipment.shopify_order_id.split('/').pop()}`;

  const handleResync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? '';
      const response = await apiFetch(`${apiUrl}/api/shipments/${shipment.id}/check`, { method: 'POST' });
      const data = await response.json();

      if (data.success) {
        setSyncResult({
          success: true,
          message: data.message || 'Tracking updated'
        });
      } else {
        setSyncResult({
          success: false,
          message: data.error || 'Sync failed'
        });
      }

      // Clear message after 5 seconds
      setTimeout(() => setSyncResult(null), 5000);
    } catch (error) {
      console.error('Resync failed:', error);
      setSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Network error'
      });
      setTimeout(() => setSyncResult(null), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveCS = async () => {
    setSavingCS(true);
    setSaveCSResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? '';
      const response = await apiFetch(`${apiUrl}/api/shipments/${shipment.id}/cs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: csStatus,
          assigned_to: csAssignedTo || null,
          contacted_customer: csContactedCustomer,
          contacted_carrier: csContactedCarrier,
          resolution: csResolution || null,
          notes: csNotes,
          is_exception: csIsException,
          exception_subtype: csIsException ? csExceptionSubtype : null,
        }),
      });
      if (!response.ok) throw new Error('Failed to save CS data');
      setCsHasChanges(false);
      setSaveCSResult({ success: true, message: 'CS data saved' });
      setTimeout(() => setSaveCSResult(null), 3000);
    } catch (error) {
      console.error('Save CS failed:', error);
      setSaveCSResult({ success: false, message: error instanceof Error ? error.message : 'Save failed' });
      setTimeout(() => setSaveCSResult(null), 5000);
    } finally {
      setSavingCS(false);
    }
  };

  const handleAddNote = () => {
    if (!newNoteText.trim()) return;
    const newNote: CSNote = {
      author: 'CS Agent', // Could be replaced with actual user from auth
      text: newNoteText.trim(),
      created_at: new Date(),
    };
    setCsNotes([newNote, ...csNotes]);
    setNewNoteText('');
    setCsHasChanges(true);
  };

  const handleDeleteNote = (index: number) => {
    setCsNotes(csNotes.filter((_, i) => i !== index));
    setCsHasChanges(true);
  };

  const markCsChanged = () => {
    setCsHasChanges(true);
  };

  const handleSendDelayNotification = async () => {
    if (!shipment || !notifyReason.trim()) return;
    setNotifySending(true);
    setNotifyResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? '';
      const response = await apiFetch(`${apiUrl}/api/shipments/${shipment.id}/notify-delay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: notifyReason.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        const msg = data.dry_run
          ? 'DRY RUN — notification recorded but email was NOT sent'
          : 'Delay notification sent';
        setNotifyResult({ success: true, message: msg });
        setNotifyModalOpen(false);
        setNotifyReason('');
      } else {
        setNotifyResult({ success: false, message: data.error || 'Failed to send notification' });
      }
    } catch {
      setNotifyResult({ success: false, message: 'Network error' });
    } finally {
      setNotifySending(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group justify="space-between" style={{ flex: 1 }}>
          <Group>
            <Title order={4}>Shipment Details</Title>
            <Badge color={RISK_COLORS[shipment.risk_level]}>
              {RISK_LABELS[shipment.risk_level]}
            </Badge>
          </Group>
          <Group gap={4}>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={onPrev}
              disabled={!hasPrev}
              title="Previous shipment"
            >
              <IconChevronUp size={18} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={onNext}
              disabled={!hasNext}
              title="Next shipment"
            >
              <IconChevronDown size={18} />
            </ActionIcon>
          </Group>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* Risk Assessment */}
        {(() => {
          const now = new Date();
          const effectiveDeadline = getActualDeadline(shipment) ?? shipment.deadline;
          const isPastDeadline = !shipment.is_delivered && effectiveDeadline.getTime() < now.getTime();
          // Filter out generic deadline risk message if past deadline
          const filteredReasons = isPastDeadline
            ? shipment.urgent_reasons.filter(r => !r.toLowerCase().includes('deadline risk'))
            : shipment.urgent_reasons;

          // Check for weather delay in recent tracking events
          const weatherKeywords = ['weather', 'storm', 'snow', 'ice', 'inclement', 'adverse conditions', 'natural disaster'];
          const hasWeatherDelay = shipment.events?.some(event => {
            const desc = (event.description || '').toLowerCase();
            return weatherKeywords.some(keyword => desc.includes(keyword));
          }) ?? false;

          const hasRisks = isPastDeadline || filteredReasons.length > 0 || hasWeatherDelay;

          if (!hasRisks) return null;

          return (
            <>
              <Box>
                <Text fw={600} mb="xs">
                  Risk Assessment
                </Text>
                <Group gap="xs" wrap="wrap">
                  {isPastDeadline && (
                    <Badge color="red" variant="filled">
                      Past deadline
                    </Badge>
                  )}
                  {hasWeatherDelay && (
                    <Badge color="blue" variant="filled">
                      Weather delay
                    </Badge>
                  )}
                  {filteredReasons.map((reason, i) => (
                    <Badge key={i} color="red" variant="light">
                      {reason}
                    </Badge>
                  ))}
                </Group>
              </Box>
              <Divider />
            </>
          );
        })()}

        {/* Recommended Actions */}
        {shipment.recommended_actions?.length > 0 && (
          <>
            <Box>
              <Text fw={600} mb="xs">
                Recommended Actions ({shipment.recommended_actions.length})
              </Text>
              <Stack gap="xs">
                {shipment.recommended_actions.map((action, i) => (
                  <Paper key={i} p="sm" radius="sm" withBorder>
                    <Group justify="space-between" align="flex-start">
                      <Box style={{ flex: 1 }}>
                        <Group gap="xs" mb={4}>
                          <Badge
                            size="xs"
                            color={action.priority === 'high' ? 'red' : action.priority === 'medium' ? 'orange' : 'blue'}
                            variant="filled"
                          >
                            {action.priority.toUpperCase()}
                          </Badge>
                          <Text size="sm" fw={600}>{action.label}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">{action.reason}</Text>
                      </Box>
                      {action.action === 'notify_customer' && !shipment.alerts_sent?.some(a => a.type === 'delay_notification') && (
                        <Button
                          size="xs"
                          variant="light"
                          color="violet"
                          onClick={(e) => {
                            e.stopPropagation();
                            const parts: string[] = [];
                            const latestEvent = shipment.events?.[0];
                            if (latestEvent?.description) parts.push(`Carrier update: ${latestEvent.description}`);
                            if (shipment.urgent_reasons?.length) parts.push(shipment.urgent_reasons.join('; '));
                            if (parts.length) setNotifyReason(parts.join('\n'));
                            setNotifyModalOpen(true);
                          }}
                        >
                          Send
                        </Button>
                      )}
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Box>
            <Divider />
          </>
        )}

        {/* Customer & Pet Section - 2 Column Layout */}
        <Box style={{ display: 'flex', gap: '1rem' }}>
          {/* Left Column: Customer Info */}
          <Box style={{ flex: 1 }}>
            <Group gap="xs" mb="xs">
              <IconUser size={16} />
              <Text fw={600}>Customer</Text>
              {Number(shipment.customer.order_count) === 1 && (
                <Badge size="xs" color="teal">
                  First Order
                </Badge>
              )}
              {Number(shipment.customer.order_count) > 1 &&
                Number(shipment.customer.order_count) <= 4 &&
                shipment.customer.customer_since &&
                (Date.now() - new Date(shipment.customer.customer_since).getTime()) <= 90 * 24 * 60 * 60 * 1000 && (
                <Badge size="xs" color="cyan">
                  New
                </Badge>
              )}
              {TIER_BADGES[shipment.customer.tier].label && (
                <Badge size="xs" color={TIER_BADGES[shipment.customer.tier].color}>
                  {TIER_BADGES[shipment.customer.tier].label} ({shipment.customer.priority_score})
                </Badge>
              )}
              {!shipment.customer.is_subscriber && (
                <>
                  <Badge size="xs" color="pink" variant="light">
                    Not Subscribed
                  </Badge>
                  {(shipment.customer.order_count >= 2 || shipment.customer.lifetime_spend >= 200) && (
                    <Badge size="xs" color="violet" variant="filled">
                      Conversion Target
                    </Badge>
                  )}
                </>
              )}
            </Group>
            <Stack gap="xs">
              <Text size="sm">
                <strong>Name:</strong> {titleCase(shipment.customer.name)}
              </Text>
              <Text size="sm">
                <strong>Email:</strong> {shipment.customer.email}
              </Text>
              <Text size="sm">
                <strong>Lifetime:</strong>{' '}
                {formatCurrency(shipment.customer.lifetime_spend)} | {shipment.customer.order_count} orders
                {shipment.customer.customer_since && (
                  <> | since {new Date(shipment.customer.customer_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</>
                )}
              </Text>
            </Stack>
          </Box>

          {/* Right Column: Pet Profiles */}
          {(petProfiles.length > 0 || loadingPets) && (
            <Box style={{ flex: 1 }}>
              {loadingPets ? (
                <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}>
                  <Text size="sm" c="dimmed">Loading pet profiles...</Text>
                </Paper>
              ) : (
                <Stack gap="xs">
                  {/* Pet selector (if multiple pets) */}
                  {petProfiles.length > 1 && (
                    <Select
                      size="xs"
                      value={selectedPetId}
                      onChange={setSelectedPetId}
                      data={petProfiles.map(p => ({
                        value: p.id,
                        label: `${p.name} (${p.type === 'dog' ? '🐕' : '🐈'} ${p.breed || p.type})`,
                      }))}
                    />
                  )}
                  {/* Selected pet details */}
                  {(() => {
                    const pet = petProfiles.find(p => p.id === selectedPetId);
                    if (!pet) return null;

                    // Calculate age
                    const birthDate = new Date(pet.date_of_birth);
                    const ageMs = Date.now() - birthDate.getTime();
                    const ageMonths = Math.floor(ageMs / (1000 * 60 * 60 * 24 * 30.44));
                    const ageYears = Math.floor(ageMonths / 12);
                    const remainingMonths = ageMonths % 12;
                    const ageStr = ageYears > 0
                      ? `${ageYears}y ${remainingMonths}mo`
                      : `${ageMonths}mo`;

                    // Portion label
                    const portionLabel = pet.portion_size === 1 ? 'Full Diet'
                      : pet.portion_size === 0.75 ? '75% Topper'
                      : pet.portion_size === 0.5 ? '50% Topper'
                      : pet.portion_size === 0.25 ? '25% Topper'
                      : `${Math.round(pet.portion_size * 100)}%`;

                    // Find meal plan for this pet
                    const petMealPlan = mealPlans.find(mp => mp.pet_profile_id === pet.id);

                    // Frequency label
                    const freqLabel = petMealPlan?.frequency === '2wks' ? '2 weeks'
                      : petMealPlan?.frequency === '4wks' ? '4 weeks'
                      : petMealPlan?.frequency === '8wks' ? '8 weeks'
                      : petMealPlan?.frequency;

                    return (
                      <Paper
                        p="sm"
                        withBorder
                        style={{
                          backgroundColor: '#e6f7f5',
                          borderColor: '#b3e0db',
                        }}
                      >
                        <Group gap="xs" mb={4}>
                          <IconPaw size={14} />
                          <Text fw={600} size="sm">
                            {pet.type === 'dog' ? '🐕' : '🐈'} {pet.name}
                          </Text>
                          {pet.breed && (
                            <Text size="xs" c="dimmed">({pet.breed})</Text>
                          )}
                        </Group>
                        <Stack gap={2}>
                          <Text size="xs">
                            {ageStr} | {pet.weight_lbs} lbs | {pet.gender} | {pet.body_condition}
                          </Text>
                          <Text size="xs">
                            <strong>{Math.round(pet.calories_per_day)} kcal/day</strong> ({portionLabel})
                          </Text>
                          {pet.health_conditions.length > 0 && (
                            <Group gap={4} mt={4}>
                              {pet.health_conditions.slice(0, 3).map((condition, i) => (
                                <Badge key={i} size="xs" variant="outline" color="orange">
                                  {condition}
                                </Badge>
                              ))}
                              {pet.health_conditions.length > 3 && (
                                <Text size="xs" c="dimmed">+{pet.health_conditions.length - 3} more</Text>
                              )}
                            </Group>
                          )}
                          {/* Meal Plan */}
                          {petMealPlan && (
                            <Box mt={6} pt={6} style={{ borderTop: '1px dashed #b3e0db' }}>
                              <Text size="xs" fw={500} mb={2}>
                                Saved Meal Plan ({freqLabel})
                              </Text>
                              <Group gap={4} wrap="wrap">
                                {petMealPlan.bundle.slice(0, 4).map((item, i) => (
                                  <Badge key={i} size="xs" variant="light" color="teal">
                                    {item.qty}× {item.handle.replace(/-/g, ' ')}
                                  </Badge>
                                ))}
                                {petMealPlan.bundle.length > 4 && (
                                  <Text size="xs" c="dimmed">+{petMealPlan.bundle.length - 4} more</Text>
                                )}
                              </Group>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })()}
                </Stack>
              )}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Shipment Section */}
        <Box>
          <Group gap="xs" mb="xs" justify="space-between">
            <Group gap="xs">
              <IconPackage size={16} />
              <Text fw={600}>Shipment</Text>
            </Group>
            {shipment.market === 'usa' && <Text>🇺🇸</Text>}
          </Group>
          <Stack gap="xs">
            <Text size="sm">
              <strong>Status:</strong>{' '}
              {shipment.events?.length > 0 ? (
                <>
                  <Anchor
                    component="button"
                    type="button"
                    onClick={() => document.getElementById('tracking-history')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    {shipment.events[0].description}
                  </Anchor>
                  <Text span size="xs" c="dimmed">
                    {' '}[{formatDate(shipment.events[0].event_time)}]
                  </Text>
                </>
              ) : (
                STATUS_LABELS[shipment.status] ?? shipment.status
              )}
            </Text>
            {(shipment.current_location || shipment.status === 'awaiting_fulfillment' || shipment.status === 'pending') && (
              <Text size="sm">
                <strong>Current Location:</strong>{' '}
                {(() => {
                  if (shipment.current_location) {
                    // Format Loomis ETOBICOKE as "Loomis ETOB DC"
                    if (shipment.current_location.toUpperCase() === 'ETOBICOKE' &&
                        (shipment.carrier_code === 'loomis' || shipment.carrier_code === 'gtagsm-loomis')) {
                      return 'Loomis ETOB DC';
                    }
                    return formatLocation(shipment.current_location);
                  }
                  return shipment.market === 'usa' ? 'Revive PENN DC' : 'Revive ETOB DC';
                })()}
                {shipment.current_location && (() => {
                  const dist = getDistanceToDestination(shipment.current_location, shipment.shipping_address.city);
                  return dist !== null ? ` [${dist} km]` : '';
                })()}
              </Text>
            )}
            <Text size="sm">
              <strong>Carrier:</strong>{' '}
              {shipment.carrier_code
                ? CARRIER_NAMES[shipment.carrier_code] ?? shipment.carrier_code
                : 'Not assigned'}
            </Text>
            <Text size="sm" mb="xs">
              <strong>Tracking:</strong>{' '}
              {shipment.tracking_number ? (
                <Anchor href={shipment.tracking_url ?? '#'} target="_blank">
                  {shipment.tracking_number}
                </Anchor>
              ) : (
                'No tracking yet'
              )}
            </Text>
            <Table withRowBorders={false} verticalSpacing={2} style={{ margin: 0 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '12%', paddingLeft: 0 }}></Table.Th>
                  <Table.Th style={{ width: '22%' }}>Ship Date</Table.Th>
                  <Table.Th style={{ width: '22%' }}>Transit</Table.Th>
                  <Table.Th style={{ width: '22%' }}>Deadline</Table.Th>
                  <Table.Th style={{ width: '22%' }}>Remain</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td style={{ textAlign: 'right', paddingRight: '0.5rem', paddingLeft: 0 }}><Text size="sm" c="dimmed">Est</Text></Table.Td>
                  <Table.Td><Text size="sm">{formatDateOnly(shipment.expected_ship_date)}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {shipment.transit_days === 0 ? 'Same Day' : `${shipment.transit_days}D`}
                      {' '}
                      <Text span size="xs" c="dimmed">
                        ({shipment.shipping_option?.toUpperCase() || 'STD'})
                      </Text>
                    </Text>
                  </Table.Td>
                  <Table.Td><Text size="sm">{formatDate(shipment.deadline)}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {(() => {
                        // If actual deadline is available, don't show live countdown on Est row
                        if (getActualDeadline(shipment)) return '-';
                        const now = new Date();
                        const deadline = shipment.deadline;
                        const diffMs = deadline.getTime() - now.getTime();
                        const absDiffMs = Math.abs(diffMs);
                        const sign = diffMs < 0 ? '-' : '+';
                        const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
                        const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
                        if (hours >= 24) {
                          const days = Math.floor(hours / 24);
                          const remHours = hours % 24;
                          return `${sign}${days}d ${remHours}h`;
                        }
                        return `${sign}${hours}h ${minutes}m`;
                      })()}
                    </Text>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ textAlign: 'right', paddingRight: '0.5rem', paddingLeft: 0 }}><Text size="sm" c="dimmed">Actual</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm">{shipment.picked_up_at ? formatDateOnly(shipment.picked_up_at) : '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{shipment.picked_up_at ? calculateTimeInTransit(shipment) : '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {(() => {
                      if (shipment.delivered_at) return <Text size="sm">{formatDate(shipment.delivered_at)}</Text>;
                      const actDeadline = getActualDeadline(shipment);
                      if (actDeadline) return <Text size="sm">{formatDate(actDeadline)}</Text>;
                      return <Text size="sm">-</Text>;
                    })()}
                  </Table.Td>
                  <Table.Td>
                    {(() => {
                      const actDeadline = getActualDeadline(shipment);
                      const deadline = actDeadline ?? shipment.deadline;
                      if (shipment.delivered_at) {
                        const diffMs = deadline.getTime() - shipment.delivered_at.getTime();
                        const absDiffMs = Math.abs(diffMs);
                        const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
                        const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
                        if (diffMs >= 0) {
                          return <Badge size="xs" color="green">{hours > 0 ? `+${hours}h early` : 'On Time'}</Badge>;
                        } else if (hours < 24) {
                          return <Badge size="xs" color="orange">-{hours}h {minutes}m</Badge>;
                        } else {
                          const days = Math.floor(hours / 24);
                          return <Badge size="xs" color="red">-{days}d late</Badge>;
                        }
                      }
                      if (actDeadline) {
                        const now = new Date();
                        const diffMs = actDeadline.getTime() - now.getTime();
                        const absDiffMs = Math.abs(diffMs);
                        const sign = diffMs < 0 ? '-' : '+';
                        const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
                        const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
                        if (hours >= 24) {
                          const days = Math.floor(hours / 24);
                          const remHours = hours % 24;
                          return <Text size="sm">{sign}{days}d {remHours}h</Text>;
                        }
                        return <Text size="sm">{sign}{hours}h {minutes}m</Text>;
                      }
                      return <Text size="sm">-</Text>;
                    })()}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Stack>
        </Box>

        <Divider />

        {/* Order Section */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconShoppingCart size={16} />
            <Text fw={600}>Order</Text>
            {shipment.is_legacy_fulfillment && (
              <Badge color="blue" variant="filled" size="sm">
                TUES ONLY
              </Badge>
            )}
          </Group>
          {/* 2-column layout: order details left, tags right */}
          <Box style={{ display: 'flex', gap: '1rem' }}>
            {/* Left column: Order details */}
            <Stack gap="xs" style={{ flex: 1 }}>
              <Text size="sm">
                <strong>Order #:</strong>{' '}
                <Anchor href={shopifyUrl} target="_blank">
                  {shipment.shopify_order_name}
                </Anchor>
              </Text>
              <Text size="sm">
                <strong>Order Date:</strong> {formatDate(shipment.order_date || shipment.created_at)}
              </Text>
              <Text size="sm">
                <strong>Order Value:</strong> {formatCurrency(shipment.order_total || 0)}
              </Text>
              {shipment.is_subscription && (
                <Text size="sm">
                  <strong>Subscription:</strong>{' '}
                  {shipment.subscription_id ? (
                    <Anchor
                      href={`https://admin.shopify.com/store/tom-sawyer/apps/bundlepayments/subscriptions/${shipment.subscription_id}`}
                      target="_blank"
                      size="sm"
                    >
                      #{shipment.subscription_id}
                    </Anchor>
                  ) : (
                    <Text span size="sm" c="dimmed">(ID not available)</Text>
                  )}
                </Text>
              )}
            </Stack>
            {/* Right column: Order tags */}
            {shipment.order_tags && shipment.order_tags.length > 0 && (
              <Box style={{ flex: 1 }}>
                <Group gap={6} wrap="wrap">
                  {shipment.order_tags.map((tag, i) => (
                    <Badge key={i} size="sm" variant="light" color="gray">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}
          </Box>
          {/* Manifest/boxes below the 2-column section */}
          <Box mt="xs">
            {shipment.manifest && shipment.manifest.boxes.length > 0 ? (
              <Stack gap="xs" mt="xs">
                {shipment.manifest.boxes.map((box, idx) => (
                  <Box
                    key={idx}
                    style={{
                      border: '1px solid #d9c9a8',
                      borderRadius: 'var(--mantine-radius-sm)',
                      padding: '0.75rem 2rem 0.75rem 1rem',
                      position: 'relative',
                      backgroundColor: '#f9f5ed',
                    }}
                  >
                    {/* Label badge - top right corner overlay */}
                    {box.label && (
                      <Badge
                        size="sm"
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: '#c4a35a',
                          color: 'white',
                        }}
                      >
                        {box.label.replace(/_/g, ' ')}
                      </Badge>
                    )}
                    {/* 2-column layout: box info left, picklist right */}
                    <Group align="flex-start" wrap="nowrap" style={{ width: '100%' }}>
                      {/* Left column: box details */}
                      <Box style={{ flex: '0 0 33%' }}>
                        <Text size="sm">
                          <strong>Meals:</strong> {box.items
                            .filter(item => !item.name?.toLowerCase().includes('welcome') && !item.sku?.toLowerCase().includes('welcome'))
                            .reduce((sum, item) => sum + item.quantity, 0)}
                        </Text>
                        <Text size="sm">
                          <strong>Size:</strong> {box.size.charAt(0).toUpperCase() + box.size.slice(1)}
                        </Text>
                        <Text size="sm">
                          <strong>Liner:</strong> {box.liner}
                        </Text>
                      </Box>
                      {/* Right column: picklist as 2-col KVP */}
                      <Group align="flex-start" gap="xs" wrap="nowrap" style={{ flex: '0 0 66%' }}>
                        <Text size="sm"><strong>Pick List:</strong></Text>
                        <Box>
                          {[...box.items]
                            .sort((a, b) => b.quantity - a.quantity)
                            .map((item, itemIdx) => (
                            <Text key={itemIdx} size="sm">
                              {item.quantity}× {item.name || item.sku}
                            </Text>
                          ))}
                        </Box>
                      </Group>
                    </Group>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Text size="sm">
                <strong>Boxes:</strong> {shipment.box_count}
              </Text>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Location Section */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconMapPin size={16} />
            <Text fw={600}>Delivery Address</Text>
          </Group>
          {(() => {
            const mapUrl = buildStaticMapUrl(shipment.shipping_address);
            return mapUrl ? (
              <Anchor href={buildMapUrl(shipment.shipping_address)} target="_blank">
                <Image
                  src={mapUrl}
                  alt="Map"
                  radius="sm"
                  mb="xs"
                  h={150}
                  fit="cover"
                  fallbackSrc="https://placehold.co/400x200?text=Map"
                />
              </Anchor>
            ) : null;
          })()}
          <Stack gap="xs">
            {/* Destination & Distance on one row */}
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm">
                <strong>Destination:</strong>{' '}
                {titleCase(shipment.shipping_address.city)},{' '}
                {REGION_ABBREV[shipment.shipping_address.province] ||
                  shipment.shipping_address.province}
              </Text>
              <Text size="sm" c="dimmed">
                {(() => {
                  // Use cached distance from backend if available
                  if (shipment.distance_from_dtc !== null && shipment.distance_from_dtc !== undefined) {
                    return `${shipment.distance_from_dtc} km from DTC`;
                  }
                  // Fallback to client-side calculation
                  const dist = getDistanceFromDTC(shipment.shipping_address.city, shipment.shipping_address.province, shipment.market);
                  return dist !== null ? `${dist} km from DTC` : '';
                })()}
              </Text>
            </Group>
            {/* Full Address */}
            <Box>
              <Text size="sm">{titleCase(shipment.shipping_address.address1)}</Text>
              {shipment.shipping_address.address2 && (
                <Text size="sm">{titleCase(shipment.shipping_address.address2)}</Text>
              )}
              <Text size="sm">
                {titleCase(shipment.shipping_address.city)}, {shipment.shipping_address.province}{' '}
                {shipment.shipping_address.postal_code.toUpperCase()}
              </Text>
              <Text size="sm" c="dimmed">
                {shipment.shipping_address.country}
              </Text>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Tracking Timeline */}
        <Box id="tracking-history">
          <Group gap="xs" mb="xs" justify="space-between">
            <Group gap="xs">
              <IconClock size={16} />
              <Text fw={600}>Tracking History</Text>
            </Group>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={handleResync}
              loading={syncing}
            >
              Resync
            </Button>
          </Group>
          {syncResult && (
            <Alert
              icon={syncResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
              color={syncResult.success ? 'green' : 'red'}
              variant="light"
              mb="sm"
              withCloseButton
              onClose={() => setSyncResult(null)}
            >
              {syncResult.message}
            </Alert>
          )}
          {shipment.events.length === 0 ? (
            <Text size="sm" c="dimmed">
              No tracking events yet
            </Text>
          ) : (
            <Timeline active={0} bulletSize={20} lineWidth={2}>
              {shipment.events.slice(0, 10).map((event, i) => (
                <Timeline.Item
                  key={i}
                  bullet={<IconTruck size={12} />}
                  title={<span style={{ textTransform: 'none' }}>{event.description}</span>}
                >
                  <Text size="xs" c="dimmed">
                    {formatLocation(event.location)} - {formatDate(event.event_time)}
                  </Text>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Box>

        <Divider />

        {/* CS Workflow Section */}
        <Box id="cs">
          <Group gap="xs" mb="md" justify="space-between">
            <Group gap="xs">
              <IconHeadset size={16} />
              <Text fw={600}>Customer Service</Text>
              {csStatus !== 'none' && (
                <Badge size="sm" color={CS_STATUS_COLORS[csStatus]}>
                  {CS_STATUS_OPTIONS.find(o => o.value === csStatus)?.label}
                </Badge>
              )}
            </Group>
            <Button
              size="xs"
              onClick={handleSaveCS}
              loading={savingCS}
              disabled={!csHasChanges}
            >
              Save
            </Button>
          </Group>
          {saveCSResult && (
            <Alert
              icon={saveCSResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
              color={saveCSResult.success ? 'green' : 'red'}
              variant="light"
              mb="sm"
              withCloseButton
              onClose={() => setSaveCSResult(null)}
            >
              {saveCSResult.message}
            </Alert>
          )}

          <Stack gap="md">
            <Group align="center" gap="md">
              <Switch
                label="Exception"
                checked={csIsException}
                onChange={(e) => { setCsIsException(e.currentTarget.checked); if (!e.currentTarget.checked) setCsExceptionSubtype(null); markCsChanged(); }}
                size="sm"
                color="red"
              />
              {csIsException && (
                <Select
                  placeholder="Type"
                  data={[
                    { value: 'missing', label: 'Missing' },
                    { value: 'rescheduled', label: 'Rescheduled' },
                    { value: 'hold', label: 'Hold' },
                    { value: 'undelivered', label: 'Undelivered' },
                  ]}
                  value={csExceptionSubtype}
                  onChange={(v) => { setCsExceptionSubtype(v as ExceptionSubtype); markCsChanged(); }}
                  size="sm"
                  w={150}
                />
              )}
            </Group>

            <Group grow>
              <Select
                label="Status"
                data={CS_STATUS_OPTIONS}
                value={csStatus}
                onChange={(v) => { setCsStatus(v as CSStatus); markCsChanged(); }}
                size="sm"
              />
              <Select
                label="Assigned To"
                data={CS_STAFF_OPTIONS}
                value={csAssignedTo}
                onChange={(v) => { setCsAssignedTo(v || ''); markCsChanged(); }}
                size="sm"
                clearable
              />
            </Group>

            {/* Customer Contact / Delay Notification */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Customer Contact</Text>
              {(() => {
                const delayAlert = shipment.alerts_sent?.find(
                  (a) => a.type === 'delay_notification'
                );
                if (delayAlert) {
                  return (
                    <Alert variant="light" color="green" icon={<IconCheck size={16} />} mb="xs">
                      <Text size="sm" fw={500}>Delay notification sent</Text>
                      <Text size="xs" c="dimmed">
                        {formatDate(delayAlert.sent_at)} &mdash; {delayAlert.message}
                      </Text>
                    </Alert>
                  );
                }
                return (
                  <Button
                    variant="light"
                    color="orange"
                    size="xs"
                    leftSection={<IconMail size={16} />}
                    onClick={() => {
                      const parts: string[] = [];
                      const latestEvent = shipment.events?.[0];
                      if (latestEvent?.description) parts.push(`Carrier update: ${latestEvent.description}`);
                      if (shipment.urgent_reasons?.length) parts.push(shipment.urgent_reasons.join('; '));
                      if (parts.length) setNotifyReason(parts.join('\n'));
                      setNotifyModalOpen(true);
                      setNotifyResult(null);
                    }}
                    disabled={!shipment.customer?.email}
                    mb="xs"
                  >
                    Send Delay Notification
                  </Button>
                );
              })()}
              {notifyResult && (
                <Alert
                  icon={notifyResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
                  color={notifyResult.success ? 'green' : 'red'}
                  variant="light"
                  mb="xs"
                  withCloseButton
                  onClose={() => setNotifyResult(null)}
                >
                  {notifyResult.message}
                </Alert>
              )}
              <Group mt="xs">
                <Checkbox
                  label="Contacted Customer"
                  checked={csContactedCustomer}
                  onChange={(e) => { setCsContactedCustomer(e.target.checked); markCsChanged(); }}
                  size="sm"
                />
                <Checkbox
                  label="Contacted Carrier"
                  checked={csContactedCarrier}
                  onChange={(e) => { setCsContactedCarrier(e.target.checked); markCsChanged(); }}
                  size="sm"
                />
              </Group>
            </Box>

            <TextInput
              label="Resolution"
              placeholder="How was this resolved?"
              value={csResolution}
              onChange={(e) => { setCsResolution(e.target.value); markCsChanged(); }}
              size="sm"
            />

            {/* Notes Section */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Notes</Text>
              <Group gap="xs" mb="sm">
                <Textarea
                  placeholder="Add a note..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  size="sm"
                  autosize
                  minRows={1}
                  maxRows={3}
                  style={{ flex: 1 }}
                />
                <ActionIcon
                  variant="filled"
                  color="blue"
                  onClick={handleAddNote}
                  disabled={!newNoteText.trim()}
                  size="lg"
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>

              {csNotes.length === 0 ? (
                <Text size="sm" c="dimmed">No notes yet</Text>
              ) : (
                <Stack gap="xs">
                  {csNotes.map((note, i) => (
                    <Paper key={i} p="xs" withBorder>
                      <Group justify="space-between" align="flex-start">
                        <Box style={{ flex: 1 }}>
                          <Text size="sm">{note.text}</Text>
                          <Text size="xs" c="dimmed">
                            {note.author} - {formatDate(note.created_at instanceof Date ? note.created_at : new Date(note.created_at))}
                          </Text>
                        </Box>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => handleDeleteNote(i)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>


            {/* Delay Notification Confirmation Modal */}
            <Modal
              opened={notifyModalOpen}
              onClose={() => setNotifyModalOpen(false)}
              title="Send Delivery Delayed Notification"
              centered
            >
              <Stack gap="md">
                <Box>
                  <Text size="sm"><strong>Customer:</strong> {shipment.customer?.name}</Text>
                  <Text size="sm"><strong>Email:</strong> {shipment.customer?.email}</Text>
                  <Text size="sm"><strong>Order:</strong> {shipment.shopify_order_name}</Text>
                </Box>
                <Textarea
                  label="Delay Reason"
                  placeholder="e.g. Carrier reported weather delay in transit"
                  value={notifyReason}
                  onChange={(e) => setNotifyReason(e.target.value)}
                  autosize
                  minRows={2}
                  maxRows={4}
                  required
                />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setNotifyModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    color="orange"
                    leftSection={<IconMail size={16} />}
                    onClick={handleSendDelayNotification}
                    loading={notifySending}
                    disabled={!notifyReason.trim()}
                  >
                    Send Notification
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}
